import { outflowManyOffers } from 'reactApp/appHelpers/featuresHelpers/features/outflowManyOffers';
import { overquotaNewUnsub, updatedUnsubScreensEnabled } from 'reactApp/appHelpers/featuresHelpers/features/overquotaNewUnsub';
import { isTrialOutflowMobile } from 'reactApp/appHelpers/featuresHelpers/features/trialOutflowMobile';
import type { BillingDataSubscriptionInfo } from 'reactApp/modules/billingData/billingData.types';
import { manyOffers } from 'reactApp/modules/subscriptionRenewal/outflowOffers/manyOffers';
import type { OutflowOfferMobileProps } from 'reactApp/modules/subscriptionRenewal/outflowOffers/offers.types';
import { cancelAutoRenewOfferMobile } from 'reactApp/modules/subscriptionRenewal/outflowOffers/offers/cancelAutoRenew.offer';
import { captchaMobile } from 'reactApp/modules/subscriptionRenewal/outflowOffers/offers/captcha.offer';
import { captchaAndSurveyMobile } from 'reactApp/modules/subscriptionRenewal/outflowOffers/offers/captchaAndSurvey';
import { checkDiscountOffer, discountOfferMobile } from 'reactApp/modules/subscriptionRenewal/outflowOffers/offers/discount.offer';
import { checkFamilyOffer, familyOfferMobile } from 'reactApp/modules/subscriptionRenewal/outflowOffers/offers/family.offer';
import { overquotaFilesOffer } from 'reactApp/modules/subscriptionRenewal/outflowOffers/offers/overquotaFiles.offer';
import {
    checkOverquotaWarningOffer,
    overquotaWarningOffer,
} from 'reactApp/modules/subscriptionRenewal/outflowOffers/offers/overquotaWarning.offer';
import { checkPauseOffer, pauseOfferMobile } from 'reactApp/modules/subscriptionRenewal/outflowOffers/offers/pause.offer';
import { surveyOfferMobile } from 'reactApp/modules/subscriptionRenewal/outflowOffers/offers/survey.offer';
import { checkTrialOffer, trialOfferMobile } from 'reactApp/modules/subscriptionRenewal/outflowOffers/offers/trial.offer';
import {
    checkUnlimitedAutoUploadOffer,
    unlimitedAutoUploadOfferMobile,
} from 'reactApp/modules/subscriptionRenewal/outflowOffers/offers/unlimitedAutoUpload.offer';
import { checkVkMusicOffer, vkMusicOfferMobile } from 'reactApp/modules/subscriptionRenewal/outflowOffers/offers/vkMusic.offer';
import { createOfferProcessor } from 'reactApp/modules/subscriptionRenewal/outflowOffers/subscriptionRenewal.offers';
import { UserQuotaSelectors } from 'reactApp/modules/userQuota/userQuota.selectors';
import { isVkMusicSubscription } from 'reactApp/sections/SubscriptionsPage/SubscriptionsPage.helpers';
import { store as reduxStore } from 'reactApp/store';

import { OutflowOffers } from './offers.types';

/**
 * @description Регистрация порядка показа предложений при отписке для тач и вебвью версии
 *
 * @param subscription - подписка
 * @returns массив указывающий какие оферы и в каком порядке будут показаны
 */
export function getMobileOutflowOffers(subscription: BillingDataSubscriptionInfo) {
    const firstOffer = isTrialOutflowMobile && subscription.isTrial ? OutflowOffers.TRIAL : OutflowOffers.ARE_YOU_SURE;

    if (isVkMusicSubscription(subscription)) {
        return [firstOffer, OutflowOffers.SURVEY, OutflowOffers.VK_MUSIC, OutflowOffers.UNLIMITED_AUTOUPLOAD, OutflowOffers.CAPTCHA];
    }

    // tempexp_19963-start
    if (overquotaNewUnsub.enable) {
        const state = reduxStore.getState();
        const used = UserQuotaSelectors.getUsed(state);

        const availableSpace = UserQuotaSelectors.getTotalOrBase(state);
        const spaceLeft = availableSpace - subscription.space.original;

        if (used?.total && used.total.original > spaceLeft) {
            return [
                firstOffer,
                OutflowOffers.OVERQUOTA_WARNING,
                OutflowOffers.OVERQUOTA_FILES,
                OutflowOffers.DISCOUNT,
                OutflowOffers.SURVEY,
                OutflowOffers.CAPTCHA,
            ];
        }
    }
    if (updatedUnsubScreensEnabled) {
        return [
            firstOffer,
            OutflowOffers.SURVEY,
            OutflowOffers.UNLIMITED_AUTOUPLOAD,
            OutflowOffers.PAUSE,
            OutflowOffers.DISCOUNT,
            OutflowOffers.CAPTCHA,
        ];
    }
    // tempexp_19963-end

    if (outflowManyOffers.enabled) {
        return manyOffers();
    }
    return [firstOffer, OutflowOffers.PAUSE, OutflowOffers.UNLIMITED_AUTOUPLOAD, OutflowOffers.CAPTCHA_WITH_SURVEY];
}

/**
 * Регистрация предложений для показа
 * @param offer - функция показывающая оффер
 * @param check - функция определяющая доступность показа оффера
 */
export const { processOuflowOfferList: processMobileOuflowOfferList } = createOfferProcessor<OutflowOfferMobileProps>({
    [OutflowOffers.ARE_YOU_SURE]: {
        offer: cancelAutoRenewOfferMobile,
    },
    [OutflowOffers.VK_MUSIC]: {
        check: checkVkMusicOffer,
        offer: vkMusicOfferMobile,
    },
    [OutflowOffers.PAUSE]: {
        check: checkPauseOffer,
        offer: pauseOfferMobile,
    },
    [OutflowOffers.DISCOUNT]: {
        check: checkDiscountOffer,
        offer: discountOfferMobile,
    },
    [OutflowOffers.UNLIMITED_AUTOUPLOAD]: {
        check: checkUnlimitedAutoUploadOffer,
        offer: unlimitedAutoUploadOfferMobile,
    },
    [OutflowOffers.FAMILY]: {
        check: checkFamilyOffer,
        offer: familyOfferMobile,
    },
    [OutflowOffers.CAPTCHA_WITH_SURVEY]: {
        offer: captchaAndSurveyMobile,
    },
    [OutflowOffers.CAPTCHA]: {
        offer: captchaMobile,
    },
    [OutflowOffers.SURVEY]: {
        offer: surveyOfferMobile,
    },
    [OutflowOffers.OVERQUOTA_WARNING]: {
        check: checkOverquotaWarningOffer,
        offer: overquotaWarningOffer,
    },
    [OutflowOffers.OVERQUOTA_FILES]: {
        check: checkOverquotaWarningOffer,
        offer: overquotaFilesOffer,
    },
    [OutflowOffers.TRIAL]: {
        check: checkTrialOffer,
        offer: trialOfferMobile,
    },
});
